import { imageUrl, srcSet } from '@helpers/image';
import { Device, from } from '@helpers/media';
import LinkButton from '@stories/Components/LinkButton/LinkButton';
import { m, useReducedMotion } from 'framer-motion';
import * as React from 'react';
import ArticleSummary from '../ArticleSummary/ArticleSummary';
import S from './BannerItem.styles';
const BannerItem = ({ active, articles, content, cta, image, index, shouldPlay, tint = 0.3, title, video, loopVideo, }) => {
    const reduceMotion = useReducedMotion();
    const refVideo = React.useRef(null);
    React.useEffect(() => {
        if (!refVideo.current) {
            return;
        }
        if (active && shouldPlay) {
            refVideo.current.currentTime = 0;
            refVideo.current.play();
        }
        else {
            refVideo.current.pause();
        }
    }, [active, shouldPlay]);
    return (React.createElement(S.Container, { "aria-current": active, initial: index === 0 ? 'initial' : 'inactive', animate: active ? 'active' : 'inactive', variants: {
            inactive: {
                transition: {
                    when: 'afterChildren',
                },
                transitionEnd: {
                    display: 'none',
                },
            },
            active: {
                display: 'grid',
            },
        }, style: { '--tintAmount': tint } },
        React.createElement(S.MediaWrapper, { variants: {
                inactive: {
                    opacity: 1,
                    zIndex: 0,
                    transitionEnd: {
                        opacity: 0,
                    },
                },
                active: {
                    opacity: 1,
                    zIndex: 1,
                },
            }, transition: {
                duration: 0.5,
                ease: 'easeOut',
            } },
            image && (React.createElement("picture", null,
                React.createElement("source", { media: from(Device.Desktop), srcSet: srcSet(image, { width: 2560, height: 1440, quality: 80 }) }),
                React.createElement("source", { media: from(Device.DesktopSmall), srcSet: srcSet(image, { width: 1365, height: 767, quality: 80 }) }),
                React.createElement("source", { media: from(Device.Tablet), srcSet: srcSet(image, { width: 1279, height: 1023, quality: 80 }) }),
                React.createElement("source", { media: from(Device.MobileLarge), srcSet: srcSet(image, { width: 767, height: 1023, quality: 80 }) }),
                React.createElement(S.Image, { alt: image.altText, draggable: false, loading: index === 0 ? 'eager' : 'lazy', role: "presentation", src: imageUrl(image, { width: 479, height: 900, quality: 80 }), srcSet: srcSet(image, { width: 479, height: 900, quality: 80 }), variants: {
                        inactive: {
                            scale: 1,
                            transition: {
                                duration: 0.5,
                                ease: 'easeOut',
                            },
                        },
                        active: {
                            scale: reduceMotion ? 1 : 1.05,
                            transition: {
                                duration: 3,
                                ease: 'easeOut',
                            },
                        },
                    }, "data-mobile-only": !!video }))),
            video && (React.createElement(S.Video, { ref: refVideo, muted: true, playsInline: true, loop: loopVideo, preload: "none", poster: image
                    ? imageUrl(image, { width: 479, height: 900, rmode: 'min', quality: 60 })
                    : undefined, "data-hide-mobile": !!image },
                React.createElement("source", { src: video, type: "video/mp4" })))),
        React.createElement(S.Inner, null,
            React.createElement(S.ContentWrapper, { variants: {
                    inactive: {},
                    active: {
                        transition: {
                            delayChildren: reduceMotion ? 0 : 0.5,
                            staggerChildren: reduceMotion ? 0 : 0.15,
                        },
                    },
                } },
                React.createElement(S.Title, { as: index === 0 ? m.h1 : m.h2, variants: {
                        initial: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        inactive: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        active: {
                            opacity: 1,
                            x: 0,
                        },
                    }, transition: {
                        duration: 0.5,
                        ease: 'easeOut',
                    } }, title),
                content && (React.createElement(S.Content, { variants: {
                        initial: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        inactive: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        active: {
                            opacity: 1,
                            x: 0,
                        },
                    }, transition: {
                        duration: 0.5,
                        ease: 'easeOut',
                    } }, content)),
                cta && (React.createElement(S.CtaWrapper, { variants: {
                        initial: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        inactive: {
                            opacity: 0,
                            x: reduceMotion ? 0 : -20,
                        },
                        active: {
                            opacity: 1,
                            x: 0,
                        },
                    }, transition: {
                        duration: 0.5,
                        ease: 'easeOut',
                    } },
                    React.createElement(LinkButton, { buttonType: "inverted", href: cta.url, target: cta.target }, cta.text)))),
            articles && (React.createElement(S.ArticleList, { variants: {
                    initial: {
                        opacity: 0,
                        x: reduceMotion ? 0 : 20,
                    },
                    inactive: {
                        opacity: 0,
                        x: reduceMotion ? 0 : 20,
                    },
                    active: {
                        opacity: 1,
                        x: 0,
                        transition: {
                            delay: 0.5,
                            duration: 0.5,
                            ease: 'easeOut',
                        },
                    },
                }, transition: {
                    duration: 0.5,
                    ease: 'easeOut',
                } }, articles.map((article) => (React.createElement(ArticleSummary, { key: article.id, ...article, headingLevel: index === 0 ? 'h2' : 'h3' }))))))));
};
export default BannerItem;
